import React, {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  ArrowUpTrayIcon,
  CheckIcon,
  LockClosedIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { CUSTOM_KEY_PREFIX } from "~/shared/kpi"
import {
  toCurrency,
  toNumber,
  convertNumber,
} from "~/ui-rtk/utils/number-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import WowGrowthRow from "./WowGrowthRow"
import { useMetricDictionary } from "~/ui-rtk/hooks/metric-dictionary"
import MetricTooltip from "~/ui-rtk/components/ui/common/MetricTooltip"
import AvailabilityWarningIcon, {
  AvailabilityWarningTooltip,
} from "./AvailabilityWarningIcon"

interface KPIColumn {
  key: string
  header: string
  type?: string
  /** Set on an in-progress bucket: what it covers so far and what its YoY compares against. */
  note?: string
}

interface KPIRow {
  key?: string
  metric?: string
  type?: string
  suffix?: string
  [key: string]: string | number | undefined
}

/** One week_N column's period bucket — the exact range an inline edit upserts. */
interface ColumnBucketRange {
  key: string
  start: string
  end: string
}

/** Inline-edit metadata for custom-metric rows (built in useConnect). */
export interface CustomEditMeta {
  /** Index-aligned with the week_N columns (index i ↔ week_{i + 1}). */
  columnBuckets: ColumnBucketRange[]
  /**
   * Per metric id: whether each column may be edited inline. A cell is locked
   * when its value is a complete roll-up of finer-grained entries — writing
   * the coarse bucket would permanently shadow them (exact-match precedence).
   */
  editableCellsByMetricId: Map<string, boolean[]>
}

export interface CustomMetricValueEntry {
  startDate: string
  endDate: string
  amount: number
}

// Server bound: CompanyCustomMetricValue.amount is Decimal(10,2).
const CUSTOM_METRIC_MAX_AMOUNT = 99_999_999.99

/** Row-cell value as a number, NaN for blanks/non-numerics. */
const cellNumericValue = (value: string | number | undefined): number => {
  if (typeof value === "number") return value
  if (typeof value === "string" && value.trim() !== "") return Number(value)
  return NaN
}

interface KPITableProps {
  data: KPIRow[]
  columns: KPIColumn[]
  title: string
  showTotals?: boolean
  isExpanded?: boolean
  scrollRef?: React.RefObject<HTMLDivElement>
  onScroll?: (scrollLeft: number) => void
  onToggleExpanded?: (tableKey: string, isExpanded: boolean) => void
  tableKey?: string
  showYoYComparison?: boolean
  isLoading?: boolean
  wowMarginalLabel?: string | undefined
  marginalMetrics?: string[]
  /**
   * Row keys the sum-all-rows marginal footer (empty `marginalMetrics`) must
   * skip: typed custom-metric rows whose values are already folded into an
   * aggregate row of the same table — summing both would double-count them.
   * Ignored when `marginalMetrics` restricts the sum explicitly.
   */
  marginalExcludeKeys?: string[]
  // Custom-report inline growth: keys of metrics that render a period-over-period
  // growth row beneath their own row, and the granularity-specific label to show.
  wowGrowthMetricKeys?: string[]
  wowGrowthLabel?: string
  /** Rendered on the header's right side (e.g. the table/line/bar switcher). */
  headerControls?: React.ReactNode
  // Inline editing of custom-metric rows (`custom.<id>` keys): both props must
  // be present for the edit affordances to render.
  customEditMeta?: CustomEditMeta
  /** Rejects (after surfacing the error) to keep the row in edit mode. */
  onSaveCustomMetric?: (
    metricId: string,
    entries: CustomMetricValueEntry[],
  ) => Promise<void>
  /** Opens the manager modal scoped to the metric (CSV upload, directions). */
  onOpenCustomMetricUpload?: (metricId: string) => void
}

const KPITable: React.FC<KPITableProps> = ({
  data,
  columns,
  title,
  showTotals = true,
  isExpanded = true,
  scrollRef,
  onScroll,
  onToggleExpanded,
  tableKey,
  showYoYComparison = false,
  isLoading,
  wowMarginalLabel,
  marginalMetrics = [],
  marginalExcludeKeys = [],
  wowGrowthMetricKeys = [],
  wowGrowthLabel,
  headerControls,
  customEditMeta,
  onSaveCustomMetric,
  onOpenCustomMetricUpload,
}) => {
  // Get metric dictionary for tooltips
  const { metricMap } = useMetricDictionary()

  // Row-level inline editing of custom-metric values: one row at a time, all
  // its editable cells become inputs, and a single Save upserts the changed
  // buckets in one request (one refetch, and well under the API rate limit).
  const [editingMetricId, setEditingMetricId] = useState<string | null>(null)
  const [draftValues, setDraftValues] = useState<Record<string, string>>({})
  const [editError, setEditError] = useState<string | null>(null)
  const [editSaving, setEditSaving] = useState(false)
  // Which cell's input to focus on entering edit mode. A ref, not state: the
  // inline ref callbacks re-run on every keystroke's render, and a state value
  // would re-focus + re-select the input mid-typing.
  const editFocusKeyRef = useRef<string | null>(null)

  /** The metric id of an inline-editable custom row, else null. */
  const getEditableMetricId = (row: KPIRow): string | null => {
    if (!customEditMeta || !onSaveCustomMetric) return null
    const key = row.key
    if (!key || !key.startsWith(CUSTOM_KEY_PREFIX)) return null
    const metricId = key.slice(CUSTOM_KEY_PREFIX.length)
    return customEditMeta.editableCellsByMetricId.has(metricId)
      ? metricId
      : null
  }

  const startEditing = (row: KPIRow, metricId: string, focusKey?: string) => {
    const drafts: Record<string, string> = {}
    for (const column of columns) {
      if (column.key === "metric") continue
      const numeric = cellNumericValue(row[column.key])
      drafts[column.key] = Number.isFinite(numeric) ? String(numeric) : ""
    }
    setDraftValues(drafts)
    setEditingMetricId(metricId)
    setEditError(null)
    editFocusKeyRef.current = focusKey ?? null
  }

  const cancelEditing = () => {
    if (editSaving) return
    setEditingMetricId(null)
    setDraftValues({})
    setEditError(null)
    editFocusKeyRef.current = null
  }

  const saveEditingRow = async (row: KPIRow) => {
    const metricId = editingMetricId
    if (!metricId || !customEditMeta || !onSaveCustomMetric || editSaving) {
      return
    }
    const editableCells = customEditMeta.editableCellsByMetricId.get(metricId)
    const entries: CustomMetricValueEntry[] = []
    for (let index = 0; index < customEditMeta.columnBuckets.length; index++) {
      const bucket = customEditMeta.columnBuckets[index]
      if (editableCells?.[index] === false) continue
      const draft = (draftValues[bucket.key] ?? "").trim()
      // Empty input = leave the bucket untouched (inline editing never deletes
      // a value — an explicit 0 must be typed).
      if (draft === "") continue
      // Same sanitation as the manager modal's manual entry.
      const amount = Number(draft.replace(/[$,\s]/g, ""))
      if (!Number.isFinite(amount)) {
        setEditError(`"${draft}" is not a number`)
        return
      }
      if (Math.abs(amount) > CUSTOM_METRIC_MAX_AMOUNT) {
        setEditError("Values must be within ±99,999,999.99")
        return
      }
      const originalNumeric = cellNumericValue(row[bucket.key])
      if (Number.isFinite(originalNumeric) && originalNumeric === amount) {
        continue // unchanged
      }
      entries.push({ startDate: bucket.start, endDate: bucket.end, amount })
    }
    if (entries.length === 0) {
      cancelEditing()
      return
    }
    setEditError(null)
    setEditSaving(true)
    try {
      await onSaveCustomMetric(metricId, entries)
      setEditingMetricId(null)
      setDraftValues({})
      editFocusKeyRef.current = null
    } catch {
      // The parent surfaced the error; stay in edit mode so nothing is lost.
    } finally {
      setEditSaving(false)
    }
  }

  /** Formatted display value, mirroring the read-mode cell formatting. */
  const formatCellValue = (
    type: string | undefined,
    value: string | number | undefined,
  ): string => {
    if (type === "currency") {
      return `${toCurrency(value || 0, "--", false, true)}`
    }
    if (type === "percentage") {
      return `${toNumber(value || 0, "--", { fractionDigits: 2 })}%`
    }
    if (type === "decimal") return `${convertNumber("decimal", value || 0)}`
    return `${toNumber(value || 0)}`
  }

  // One shared tooltip per table for the data-availability warning icons —
  // flagged cells can number in the hundreds, so each icon is only an anchor.
  const availabilityTooltipId = useId()

  // Metrics that render an inline period-over-period growth row (custom reports).
  const growthKeySet = useMemo(
    () => new Set(wowGrowthMetricKeys),
    [wowGrowthMetricKeys],
  )

  // Calculate WoW marginal for spend metrics and tables with marginalMetrics specified
  const wowMarginal = useMemo(() => {
    // Show WoW Marginal for Ad Spend (when showTotals is true) or when marginalMetrics is specified
    if (!showTotals && marginalMetrics.length === 0) return null

    const wowRow: KPIRow = { metric: "WoW Marginal" }
    // In the sum-all-rows mode, skip typed custom rows whose values are
    // already inside a folded aggregate row of this table (double-count).
    // An explicit marginalMetrics list already restricts the sum, so the
    // exclusions only apply when that list is empty.
    const excludedKeys =
      marginalMetrics.length === 0 && marginalExcludeKeys.length > 0
        ? new Set(marginalExcludeKeys)
        : null
    const isSummedRow = (row: KPIRow) => {
      if (excludedKeys?.has(row.key as string)) return false
      // If marginalMetrics is specified, only include those metrics
      return (
        marginalMetrics.length === 0 ||
        marginalMetrics.includes(row.key as string)
      )
    }

    columns.forEach((column, index) => {
      if (column.key !== "metric" && index > 1) {
        // Skip first two columns (metric column and first week which has no previous week)
        const currentWeekKey = column.key
        const previousWeekKey = columns[index - 1]?.key

        if (previousWeekKey) {
          const currentTotal = data.reduce((sum, row) => {
            if (!isSummedRow(row)) return sum
            const value = parseFloat(String(row[currentWeekKey])) || 0
            return sum + value
          }, 0)

          const previousTotal = data.reduce((sum, row) => {
            if (!isSummedRow(row)) return sum
            const value = parseFloat(String(row[previousWeekKey])) || 0
            return sum + value
          }, 0)

          const marginal = currentTotal - previousTotal
          wowRow[currentWeekKey] = marginal
        } else {
          wowRow[currentWeekKey] = 0 // First week has no previous week
        }
      } else if (column.key !== "metric") {
        wowRow[column.key] = 0 // First week
      }
    })

    return wowRow
  }, [data, columns, showTotals, marginalMetrics, marginalExcludeKeys])

  // Auto-scroll right to show the latest columns — but only when the column
  // window itself changes (initial load, granularity switch). Value saves and
  // other refetches rebuild `data` with the same columns, and re-scrolling
  // then would yank the viewport away from a cell mid-edit.
  const columnsSignature = columns.map(column => column.header).join("|")
  const lastAutoScrollSignature = useRef<string | null>(null)
  useEffect(() => {
    if (
      scrollRef?.current &&
      !isLoading &&
      data.length > 0 &&
      lastAutoScrollSignature.current !== columnsSignature
    ) {
      lastAutoScrollSignature.current = columnsSignature
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
        }
      }, 100)
    }
  }, [data, isLoading, scrollRef, columnsSignature])

  // A granularity/date-window switch re-derives the columns, but drafts are
  // keyed by the positional week_N column keys — saving them against the NEW
  // columnBuckets would write old-granularity amounts into the wrong ranges.
  // Discard any in-progress edit when the column window changes.
  const lastEditSignature = useRef(columnsSignature)
  useEffect(() => {
    if (lastEditSignature.current === columnsSignature) return
    lastEditSignature.current = columnsSignature
    setEditingMetricId(null)
    setDraftValues({})
    setEditError(null)
    editFocusKeyRef.current = null
  }, [columnsSignature])

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div
            className="flex items-center cursor-pointer p-2 rounded-system-sm hover:bg-system-blue-800/30 transition-colors space-x-2"
            onClick={() => {
              const newExpanded = !isExpanded
              if (onToggleExpanded && tableKey) {
                onToggleExpanded(tableKey, newExpanded)
              }
            }}
          >
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <div className="flex items-center ">
              <svg
                className={cn(
                  "w-5 h-5 text-white transition-transform duration-200",
                  isExpanded ? "rotate-180" : "rotate-0",
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {headerControls}
        </div>
        {isExpanded && (
          <div
            ref={scrollRef}
            className="overflow-x-auto border border-system-blue-700 rounded-system-lg w-full"
          >
            <table className="min-w-full divide-y divide-system-blue-700 select-none">
              <thead className="bg-system-blue-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-center text-white uppercase tracking-wider sticky left-0 bg-system-blue-700  border-r border-system-blue-600 w-60 min-w-60 max-w-60">
                    <div className="h-4 bg-system-blue-600 rounded animate-pulse"></div>
                  </th>
                  {/* Create skeleton columns */}
                  {Array.from({ length: 12 }, (_, i) => (
                    <th
                      key={i}
                      className="px-6 py-3 text-left text-xs font-medium text-center text-white uppercase tracking-wider w-32 min-w-32 max-w-32"
                    >
                      <div className="h-4 bg-system-blue-600 rounded animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-system-blue-900 divide-y divide-system-blue-700">
                {/* Create skeleton rows */}
                {Array.from({ length: 6 }, (_, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={cn(
                      "hover:bg-system-blue-800 transition-colors",
                      rowIndex % 2 === 0
                        ? "bg-system-blue-900"
                        : "bg-system-blue-800/50",
                    )}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-center font-medium sticky left-0 bg-system-blue-700 border-r border-system-blue-600 w-60 min-w-60 max-w-60">
                      <div className="h-4 bg-system-blue-600 rounded animate-pulse"></div>
                    </td>
                    {Array.from({ length: 12 }, (_, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-6 py-4 whitespace-nowrap text-sm text-white text-center w-32 min-w-32 max-w-32"
                      >
                        <div className="h-4 bg-system-blue-600 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div
          className="flex items-center cursor-pointer p-2 rounded-system-sm hover:bg-system-blue-800/30 transition-colors space-x-2"
          onClick={() => {
            const newExpanded = !isExpanded
            if (onToggleExpanded && tableKey) {
              onToggleExpanded(tableKey, newExpanded)
            }
          }}
        >
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <div className="flex items-center ">
            <svg
              className={cn(
                "w-5 h-5 text-white transition-transform duration-200",
                isExpanded ? "rotate-180" : "rotate-0",
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {headerControls}
      </div>
      {isExpanded && (
        <div
          ref={scrollRef}
          className="overflow-x-auto border border-system-blue-700 rounded-system-lg w-full"
          onScroll={e => {
            if (onScroll) {
              onScroll(e.currentTarget.scrollLeft)
            }
          }}
        >
          <table className="min-w-full divide-y divide-system-blue-700 select-none">
            <thead className="bg-system-blue-800">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={column.key}
                    className={cn(
                      "px-6 py-3 text-left text-xs font-medium text-center text-white uppercase tracking-wider",
                      index === 0 && [
                        // z-10: the week cells' edit wrappers are positioned
                        // (relative, for the hover pencil) and would otherwise
                        // paint over this sticky cell when scrolled beneath it.
                        "sticky left-0 z-10 bg-system-blue-700 border-r border-system-blue-600",
                        "w-60 min-w-60 max-w-60 text-left",
                      ],
                      index > 0 && ["w-32 min-w-32 max-w-32 "],
                    )}
                  >
                    {column.header}
                    {column.note && (
                      <span
                        data-testid="partial-bucket-tag"
                        data-tooltip-id={availabilityTooltipId}
                        data-tooltip-content={column.note}
                        className="ml-1 text-system-warning-400 cursor-help"
                      >
                        (partial)
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-system-blue-900 divide-y divide-system-blue-700">
              {data.map((row, rowIndex) => {
                // Inline-edit affordances for custom-metric rows (hover-revealed
                // via the row's `group` class).
                const rowMetricId = getEditableMetricId(row)
                const isEditingRow =
                  rowMetricId !== null && rowMetricId === editingMetricId

                return (
                  <Fragment key={row.key || `row-${rowIndex}`}>
                    <tr
                      className={cn(
                        "group hover:bg-system-blue-800 transition-colors",
                        rowIndex % 2 === 0
                          ? "bg-system-blue-900"
                          : "bg-system-blue-800/50",
                      )}
                    >
                      {columns.map((column, colIndex) => {
                        const value = row[column.key]
                        const type = row.type
                        const isFirstCol = colIndex === 0
                        const isWeekCol = colIndex > 0
                        const cellEditable =
                          rowMetricId !== null &&
                          isWeekCol &&
                          customEditMeta?.editableCellsByMetricId.get(
                            rowMetricId,
                          )?.[colIndex - 1] !== false

                        // Data-availability warnings attached by the row builders
                        // (composite metrics whose source data is missing for
                        // this period / its comparison period).
                        const availabilityWarning = row[
                          `${column.key}_warning`
                        ] as string | undefined
                        const prevAvailabilityWarning = row[
                          `${column.key}_warning_prev`
                        ] as string | undefined

                        // Get metric metadata for first column tooltip
                        const metricKey =
                          isFirstCol && row.key
                            ? row.key.replace(/^metrics\./, "")
                            : undefined
                        const metricMetadata = metricKey
                          ? metricMap.get(metricKey)
                          : undefined

                        return (
                          <td
                            key={`${rowIndex}-${column.key}`}
                            className={cn(
                              "text-sm text-white",
                              isFirstCol ? "text-left p-0" : "text-center px-6",
                              !isFirstCol && "whitespace-nowrap",
                              showYoYComparison
                                ? "py-2"
                                : !isFirstCol && "py-4",
                              isFirstCol && [
                                // z-10: keep the sticky cell above the rows'
                                // positioned week-cell wrappers (hover pencil).
                                "font-medium sticky left-0 z-10 bg-system-blue-700",
                                "border-r border-system-blue-600",
                                "w-60 min-w-60 max-w-60",
                              ],
                              isWeekCol && ["w-32 min-w-32 max-w-32"],
                              rowIndex % 2 === 0
                                ? "bg-system-blue-900"
                                : "bg-system-blue-800",
                            )}
                          >
                            {(() => {
                              if (isFirstCol) {
                                const labelContent = metricMetadata ? (
                                  <MetricTooltip
                                    metric={metricMetadata}
                                    showIcon={true}
                                    iconSize={12}
                                    place="right"
                                    className={cn(
                                      "flex items-center gap-1 w-full h-full px-6 text-sm font-medium text-white leading-tight break-words",
                                      showYoYComparison ? "py-2" : "py-4",
                                    )}
                                  >
                                    {value}
                                  </MetricTooltip>
                                ) : (
                                  <div
                                    className={cn(
                                      "flex items-start px-6",
                                      showYoYComparison ? "py-2" : "py-4",
                                    )}
                                  >
                                    <span className="text-sm font-medium text-white leading-tight break-words">
                                      {value}
                                    </span>
                                  </div>
                                )
                                if (!rowMetricId) return labelContent
                                // Custom-metric rows: edit / CSV-upload actions in
                                // the always-visible sticky cell; Save + Cancel
                                // replace them while the row is in edit mode.
                                return (
                                  <div>
                                    <div className="flex items-start justify-between">
                                      <div className="min-w-0 flex-1">
                                        {labelContent}
                                      </div>
                                      <div
                                        className={cn(
                                          "flex items-center gap-1 pr-3",
                                          showYoYComparison ? "py-2" : "py-4",
                                          !isEditingRow &&
                                            "opacity-0 group-hover:opacity-100 transition-opacity",
                                        )}
                                      >
                                        {isEditingRow ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                void saveEditingRow(row)
                                              }
                                              disabled={editSaving}
                                              title="Save values"
                                              aria-label="Save values"
                                              className="p-1 rounded text-system-success-400 hover:bg-system-blue-600 disabled:opacity-50"
                                            >
                                              <CheckIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={cancelEditing}
                                              disabled={editSaving}
                                              title="Cancel"
                                              aria-label="Cancel editing"
                                              className="p-1 rounded text-system-blue-300 hover:text-white hover:bg-system-blue-600 disabled:opacity-50"
                                            >
                                              <XMarkIcon className="w-4 h-4" />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={() =>
                                                startEditing(row, rowMetricId)
                                              }
                                              title="Edit values"
                                              aria-label="Edit values"
                                              className="p-1 rounded text-system-blue-300 hover:text-white hover:bg-system-blue-600"
                                            >
                                              <PencilIcon className="w-3.5 h-3.5" />
                                            </button>
                                            {onOpenCustomMetricUpload && (
                                              <button
                                                onClick={() =>
                                                  onOpenCustomMetricUpload(
                                                    rowMetricId,
                                                  )
                                                }
                                                title="Upload CSV"
                                                aria-label="Upload CSV"
                                                className="p-1 rounded text-system-blue-300 hover:text-white hover:bg-system-blue-600"
                                              >
                                                <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {isEditingRow && editError && (
                                      <p className="px-6 pb-2 text-xs text-system-error-400">
                                        {editError}
                                      </p>
                                    )}
                                  </div>
                                )
                              }
                              if (isWeekCol && isEditingRow) {
                                // Row edit mode: every editable cell becomes an
                                // input (YoY sub-lines are hidden while editing);
                                // complete finer roll-ups stay read-only.
                                if (!cellEditable) {
                                  return (
                                    <div
                                      className="flex items-center justify-center gap-1 text-system-blue-300"
                                      title="Fully covered by finer-grained entries — edit those values instead (a value saved here would override them)"
                                    >
                                      <span className="font-mono text-sm">
                                        {formatCellValue(type, value)}
                                      </span>
                                      <LockClosedIcon className="w-3 h-3 shrink-0" />
                                    </div>
                                  )
                                }
                                return (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={draftValues[column.key] ?? ""}
                                    placeholder="0"
                                    disabled={editSaving}
                                    aria-label={`${row.metric ?? "Value"} — ${column.header}`}
                                    onChange={event =>
                                      setDraftValues(prev => ({
                                        ...prev,
                                        [column.key]: event.target.value,
                                      }))
                                    }
                                    onKeyDown={event => {
                                      if (event.key === "Enter") {
                                        event.preventDefault()
                                        void saveEditingRow(row)
                                      } else if (event.key === "Escape") {
                                        cancelEditing()
                                      }
                                    }}
                                    ref={element => {
                                      if (
                                        element &&
                                        editFocusKeyRef.current === column.key
                                      ) {
                                        editFocusKeyRef.current = null
                                        element.focus()
                                        element.select()
                                      }
                                    }}
                                    className="w-24 px-2 py-1 text-sm text-center font-mono bg-basic-black border border-basic-blue rounded text-white focus:outline-none focus:border-system-blue-400 disabled:opacity-50"
                                  />
                                )
                              }
                              if (isWeekCol) {
                                return (
                                  <div className="text-center relative">
                                    {rowMetricId && cellEditable && (
                                      <button
                                        onClick={() =>
                                          startEditing(
                                            row,
                                            rowMetricId,
                                            column.key,
                                          )
                                        }
                                        title="Edit value"
                                        aria-label={`Edit ${column.header} value`}
                                        className="absolute -right-4 top-1/2 -translate-y-1/2 p-0.5 rounded text-system-blue-300 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <PencilIcon className="w-3 h-3" />
                                      </button>
                                    )}
                                    {showYoYComparison ? (
                                      // Enhanced YoY comparison cell design
                                      (() => {
                                        const yoyChangeKey = `${column.key}_yoy_change`
                                        const yoyPreviousKey = `${column.key}_yoy_previous`
                                        const yoyChange = row[yoyChangeKey] as
                                          | number
                                          | undefined
                                        const previousValue =
                                          row[yoyPreviousKey]

                                        return (
                                          <div className="space-y-0.5 py-0.5">
                                            {/* Current Year Value - clean without background */}
                                            <div>
                                              {(() => {
                                                if (type === "currency") {
                                                  return (
                                                    <span className="font-mono text-white font-semibold text-sm">
                                                      {toCurrency(
                                                        value || 0,
                                                        "--",
                                                        false,
                                                        true,
                                                      )}
                                                    </span>
                                                  )
                                                }
                                                if (type === "percentage") {
                                                  return (
                                                    <span className="font-mono text-white font-semibold text-sm">
                                                      {toNumber(
                                                        value || 0,
                                                        "--",
                                                        {
                                                          fractionDigits: 2,
                                                        },
                                                      )}
                                                      %
                                                    </span>
                                                  )
                                                }
                                                if (type === "decimal") {
                                                  return (
                                                    <span className="font-mono text-white font-semibold text-sm">
                                                      {convertNumber(
                                                        "decimal",
                                                        value || 0,
                                                      )}
                                                    </span>
                                                  )
                                                }
                                                return (
                                                  <span className="font-mono text-white font-semibold text-sm">
                                                    {toNumber(value || 0)}
                                                  </span>
                                                )
                                              })()}
                                              {row.suffix || ""}
                                              {availabilityWarning && (
                                                <AvailabilityWarningIcon
                                                  tooltipId={
                                                    availabilityTooltipId
                                                  }
                                                  message={availabilityWarning}
                                                  className="ml-1"
                                                />
                                              )}
                                            </div>

                                            {/* Previous Year Value & YoY Change - minimal styling */}
                                            {previousValue !== undefined && (
                                              <div className="space-y-0.5">
                                                <div className="text-xs text-system-blue-400 font-normal">
                                                  {(() => {
                                                    if (type === "currency") {
                                                      return toCurrency(
                                                        previousValue || 0,
                                                        "--",
                                                        false,
                                                        true,
                                                      )
                                                    }
                                                    if (type === "percentage") {
                                                      return `${toNumber(previousValue || 0)}%`
                                                    }
                                                    if (type === "decimal") {
                                                      return convertNumber(
                                                        "decimal",
                                                        previousValue || 0,
                                                      )
                                                    }
                                                    return toNumber(
                                                      previousValue || 0,
                                                    )
                                                  })()}
                                                  {row.suffix || ""}
                                                  {prevAvailabilityWarning && (
                                                    <AvailabilityWarningIcon
                                                      tooltipId={
                                                        availabilityTooltipId
                                                      }
                                                      message={
                                                        prevAvailabilityWarning
                                                      }
                                                      className="ml-1"
                                                    />
                                                  )}
                                                </div>

                                                {yoyChange !== undefined &&
                                                  yoyChange !== null && (
                                                    <div
                                                      className={cn(
                                                        "flex items-center justify-center space-x-0.5 text-xs font-medium",
                                                        (() => {
                                                          if (yoyChange === 0) {
                                                            return "text-system-gray-400"
                                                          }
                                                          return yoyChange > 0
                                                            ? "text-system-success-400"
                                                            : "text-system-error-400"
                                                        })(),
                                                      )}
                                                    >
                                                      <span className="text-xs">
                                                        {(() => {
                                                          if (yoyChange === 0) {
                                                            return "="
                                                          }
                                                          return yoyChange > 0
                                                            ? "↗"
                                                            : "↘"
                                                        })()}
                                                      </span>
                                                      <span className="font-mono text-xs">
                                                        {Math.abs(
                                                          yoyChange,
                                                        ).toFixed(1)}
                                                        %
                                                      </span>
                                                    </div>
                                                  )}
                                              </div>
                                            )}
                                          </div>
                                        )
                                      })()
                                    ) : (
                                      // Single value display (current behavior)
                                      <div>
                                        {(() => {
                                          if (type === "currency") {
                                            return (
                                              <span className="font-mono text-white font-medium">
                                                {toCurrency(
                                                  value || 0,
                                                  "--",
                                                  false,
                                                  true,
                                                )}
                                              </span>
                                            )
                                          }
                                          if (type === "percentage") {
                                            return (
                                              <span className="font-mono text-white font-medium">
                                                {toNumber(value || 0, "--", {
                                                  fractionDigits: 2,
                                                })}
                                                %
                                              </span>
                                            )
                                          }
                                          if (type === "decimal") {
                                            return (
                                              <span className="font-mono text-white font-medium">
                                                {convertNumber(
                                                  "decimal",
                                                  value || 0,
                                                )}
                                              </span>
                                            )
                                          }
                                          return (
                                            <span className="font-mono text-white font-medium">
                                              {toNumber(value || 0)}
                                            </span>
                                          )
                                        })()}
                                        {row.suffix || ""}
                                        {availabilityWarning && (
                                          <AvailabilityWarningIcon
                                            tooltipId={availabilityTooltipId}
                                            message={availabilityWarning}
                                            className="ml-1"
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )
                              }
                              return (
                                <span className="text-white">
                                  {value || "-"}
                                </span>
                              )
                            })()}
                          </td>
                        )
                      })}
                    </tr>

                    {/* Inline period-over-period growth row, directly beneath the
                      metric it tracks (custom reports only). */}
                    {wowGrowthLabel && row.key && growthKeySet.has(row.key) && (
                      <WowGrowthRow
                        key={`growth-row-${row.key}`}
                        row={row}
                        columns={columns}
                        label={wowGrowthLabel}
                      />
                    )}
                  </Fragment>
                )
              })}

              {/* WoW Marginal Row - shown based on showTotals or marginalMetrics configuration */}
              {wowMarginal && (
                <tr className="bg-system-blue-900 border-t border-system-blue-900">
                  {columns.map((column, colIndex) => {
                    const value = wowMarginal[column.key] as number | undefined
                    const isFirstCol = colIndex === 0
                    const isWeekCol = colIndex > 0

                    return (
                      <td
                        key={`wow-${column.key}`}
                        className={cn(
                          "px-6 py-4 text-center whitespace-nowrap text-sm text-white",
                          isFirstCol && [
                            "sticky left-0 z-10 bg-system-blue-900",
                            "border-r border-system-blue-900",
                            "w-60 min-w-60 max-w-60",
                          ],
                          isWeekCol && ["w-32 min-w-32 max-w-32"],
                        )}
                      >
                        {(() => {
                          if (isFirstCol) {
                            return (
                              <div className="flex items-center">
                                <span className="text-sm  text-white italic">
                                  {wowMarginalLabel || "WoW Marginal"}
                                </span>
                              </div>
                            )
                          }
                          if (isWeekCol) {
                            return (
                              <div className="text-center">
                                <span
                                  className={cn(
                                    "font-mono text-white text-center text-sm italic",
                                    (() => {
                                      if ((value ?? 0) > 0) {
                                        return "text-system-success-400"
                                      }
                                      if ((value ?? 0) < 0) {
                                        return "text-system-error-400"
                                      }
                                      return ""
                                    })(),
                                  )}
                                >
                                  {(value ?? 0) > 0 ? "+" : ""}
                                  {toCurrency(value || 0, "--", false, true)}
                                </span>
                              </div>
                            )
                          }
                          return (
                            <span className="text-white">{value || "-"}</span>
                          )
                        })()}
                      </td>
                    )
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Shared tooltip for every data-availability warning icon in this table. */}
      <AvailabilityWarningTooltip id={availabilityTooltipId} />
    </div>
  )
}

export default KPITable
